import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { User, Bell, Shield, Database, Zap, Key, Copy, Eye, EyeOff, Trash2, HelpCircle, FileText, Wrench, Users as UsersIcon, Settings as SettingsIcon, Workflow, History } from 'lucide-react';
import TutorialModal from '../components/TutorialModal';
import ContactSupportModal from '../components/ContactSupportModal';
import HelpDocumentation from '../components/HelpDocumentation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user } = useAuth();
  const { t, setLanguage, language } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [showNewKey, setShowNewKey] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [visibleKeys, setVisibleKeys] = useState({});
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [contactSupportOpen, setContactSupportOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [newTpl, setNewTpl] = useState({ name: '', subject: '', body_html: '', body_text: '' });
  const [editingTpl, setEditingTpl] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [newField, setNewField] = useState({ entity: 'contact', name: '', type: 'text', options: '' });
  // Audit log
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const fetchAuditLogs = async () => {
    try {
      setAuditLoading(true);
      const res = await axios.get(`${API}/audit/logs`, { withCredentials: true });
      setAuditLogs(res.data || []);
    } catch (e) {
      // ignore (403 if not admin/owner)
    } finally {
      setAuditLoading(false);
    }
  };

  // Workspace
  const [workspace, setWorkspace] = useState({ company_name: '', timezone: 'UTC', currency: 'USD', brand_color: '#0176D3' });
  const fetchWorkspace = async () => {
    try {
      const res = await axios.get(`${API}/settings/workspace`, { withCredentials: true });
      setWorkspace({
        company_name: res.data.company_name || '',
        timezone: res.data.timezone || 'UTC',
        currency: res.data.currency || 'USD',
        brand_color: res.data.brand_color || '#0176D3',
      });
    } catch (e) {
      // ignore
    }
  };
  const saveWorkspace = async () => {
    try {
      setLoading(true);
      await axios.put(`${API}/settings/workspace`, workspace, { withCredentials: true });
      toast({ title: 'Workspace saved' });
    } catch (e) {
      toast({ title: 'Failed to save workspace', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Invites
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [invites, setInvites] = useState([]);
  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/teams/invites`, { withCredentials: true });
      setInvites(res.data || []);
    } catch {}
  };
  const sendInvite = async () => {
    if (!inviteEmail.trim()) return toast({ title: 'Email required', variant: 'destructive' });
    try {
      await axios.post(`${API}/teams/invite`, { email: inviteEmail, role: inviteRole }, { withCredentials: true });
      setInviteEmail('');
      setInviteRole('member');
      fetchInvites();
      toast({ title: 'Invite sent' });
    } catch (e) {
      toast({ title: 'Failed to send invite', variant: 'destructive' });
    }
  };

  // Pipeline
  const [stages, setStages] = useState([]);
  const [stageName, setStageName] = useState('');
  const [stageProb, setStageProb] = useState('');
  const fetchPipeline = async () => {
    try {
      const res = await axios.get(`${API}/pipelines`, { withCredentials: true });
      setStages((res.data?.stages || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch {}
  };
  const addStage = () => {
    if (!stageName.trim()) return;
    const probability = Math.max(0, Math.min(100, parseInt(stageProb || '0', 10)));
    const order = (stages[stages.length - 1]?.order || 0) + 1;
    setStages([...stages, { name: stageName.trim(), probability, order }]);
    setStageName('');
    setStageProb('');
  };
  const removeStage = (order) => setStages(stages.filter((s) => s.order !== order).map((s, idx) => ({ ...s, order: idx + 1 })));
  const savePipeline = async () => {
    try {
      await axios.put(`${API}/pipelines`, { stages }, { withCredentials: true });
      toast({ title: 'Pipeline saved' });
      fetchPipeline();
    } catch (e) {
      toast({ title: 'Failed to save pipeline', variant: 'destructive' });
    }
  };

  useEffect(() => {
    fetchApiKeys();
    fetchTemplates();
    fetchCustomFields();
    fetchWorkspace();
    fetchInvites();
    fetchPipeline();
    fetchAuditLogs();
  }, []);

  // Profile settings
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    company: '',
    timezone: 'UTC',
  });

  // Notification settings
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    dealUpdates: true,
    campaignAlerts: true,
    weeklyReport: false,
  });

  // Preferences
  const [preferences, setPreferences] = useState({
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    language: language || 'en',
    theme: 'light',
  });

  const handleProfileSave = () => {
    setLoading(true);
    setTimeout(() => {
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
      setLoading(false);
    }, 1000);
  };

  const handleNotificationsSave = () => {
    setLoading(true);
    setTimeout(() => {
      toast({
        title: "Notifications Updated",
        description: "Your notification preferences have been saved.",
      });
      setLoading(false);
    }, 1000);
  };

  const handlePreferencesSave = () => {
    setLoading(true);
    setTimeout(() => {
      toast({
        title: "Preferences Updated",
        description: "Your preferences have been saved.",
      });
      setLoading(false);
    }, 1000);
  };

  // Email templates
  const fetchTemplates = async () => {
    try {
      setTplLoading(true);
      const res = await axios.get(`${API}/emails/templates`, { withCredentials: true });
      setTemplates(res.data || []);
    } catch (e) {
      console.error('Fetch templates failed', e);
    } finally {
      setTplLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!newTpl.name.trim() || !newTpl.subject.trim()) {
      toast({ title: 'Template name and subject are required', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/emails/templates`, newTpl, { withCredentials: true });
      setNewTpl({ name: '', subject: '', body_html: '', body_text: '' });
      fetchTemplates();
      toast({ title: 'Template created' });
    } catch (e) {
      toast({ title: 'Failed to create template', variant: 'destructive' });
    }
  };

  const saveTemplate = async () => {
    if (!editingTpl) return;
    try {
      await axios.put(`${API}/emails/templates/${editingTpl.id}`, {
        name: editingTpl.name,
        subject: editingTpl.subject,
        body_html: editingTpl.body_html,
        body_text: editingTpl.body_text,
      }, { withCredentials: true });
      setEditingTpl(null);
      fetchTemplates();
      toast({ title: 'Template updated' });
    } catch (e) {
      toast({ title: 'Failed to update template', variant: 'destructive' });
    }
  };

  const deleteTemplate = async (tplId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/emails/templates/${tplId}`, { withCredentials: true });
      fetchTemplates();
      toast({ title: 'Template deleted' });
    } catch (e) {
      toast({ title: 'Failed to delete template', variant: 'destructive' });
    }
  };

  const fetchCustomFields = async () => {
    try {
      const res = await axios.get(`${API}/custom-fields`, { withCredentials: true });
      setCustomFields(res.data || []);
    } catch (e) {
      console.error('Fetch custom fields failed', e);
    }
  };

  const createCustomField = async () => {
    if (!newField.name.trim()) return toast({ title: 'Field name required', variant: 'destructive' });
    try {
      const payload = { ...newField, options: newField.options ? newField.options.split(',').map(s => s.trim()).filter(Boolean) : undefined };
      await axios.post(`${API}/custom-fields`, payload, { withCredentials: true });
      setNewField({ entity: 'contact', name: '', type: 'text', options: '' });
      fetchCustomFields();
      toast({ title: 'Custom field created' });
    } catch (e) {
      toast({ title: 'Failed to create field', variant: 'destructive' });
    }
  };

  const deleteCustomField = async (id) => {
    if (!window.confirm('Delete this custom field?')) return;
    try {
      await axios.delete(`${API}/custom-fields/${id}`, { withCredentials: true });
      fetchCustomFields();
    } catch (e) {
      toast({ title: 'Failed to delete field', variant: 'destructive' });
    }
  };

  const handleExportData = () => {
    toast({
      title: "Export Started",
      description: "Your data export has been initiated. You'll receive an email when it's ready.",
    });
  };

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/api-keys`, { withCredentials: true });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a name for your API key",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api-keys`,
        { name: newKeyName },
        { withCredentials: true }
      );
      setShowNewKey(response.data);
      setNewKeyName('');
      fetchApiKeys();
      toast({
        title: "API Key Created",
        description: "Your new API key has been generated. Copy it now - it won't be shown again!",
      });
    } catch (error) {
      console.error('Error creating API key:', error);
      toast({
        title: "Error",
        description: "Failed to create API key",
        variant: "destructive",
      });
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { withCredentials: true });
      fetchApiKeys();
      toast({
        title: "API Key Deleted",
        description: "The API key has been permanently deleted.",
      });
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast({
        title: "Error",
        description: "Failed to delete API key",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "API key copied to clipboard",
    });
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    return key.substring(0, 8) + '...' + key.substring(key.length - 4);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="px-4 lg:px-6 max-w-6xl mx-auto pb-24">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-2">Manage your account settings and preferences</p>
      </div>

      <Tabs defaultValue={searchParams.get('tab') || 'profile'} onValueChange={(val) => setSearchParams({ tab: val })} className="space-y-6">
        <div className="overflow-x-auto">
        <TabsList className="grid w-full grid-cols-13 lg:w-auto min-w-[980px]">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="workspace" className="flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Workspace</span>
          </TabsTrigger>
          <TabsTrigger value="users-teams" className="flex items-center gap-2">
            <UsersIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Users & Teams</span>
          </TabsTrigger>
          <TabsTrigger value="pipelines" className="flex items-center gap-2">
            <Workflow className="w-4 h-4" />
            <span className="hidden sm:inline">Pipelines</span>
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            <span className="hidden sm:inline">API Keys</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Notifications</span>
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            <span className="hidden sm:inline">Preferences</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            <span className="hidden sm:inline">Data</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Email Templates</span>
          </TabsTrigger>
          <TabsTrigger value="custom-fields" className="flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            <span className="hidden sm:inline">Custom Fields</span>
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            <span className="hidden sm:inline">Integrations</span>
          </TabsTrigger>
          <TabsTrigger value="audit-log" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">Audit Log</span>
          </TabsTrigger>
          <TabsTrigger value="help" className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Help</span>
          </TabsTrigger>
        </TabsList>
        </div>
        {/* Workspace Tab */}
        <TabsContent value="workspace">
          <Card>
            <CardHeader>
              <CardTitle>Workspace</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input placeholder="Bissa" value={workspace.company_name} onChange={(e) => setWorkspace({ ...workspace, company_name: e.target.value })} />
                </div>
                <div>
                  <Label>Timezone</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={workspace.timezone} onChange={(e) => setWorkspace({ ...workspace, timezone: e.target.value })}>
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="America/Los_Angeles">America/Los_Angeles</option>
                  </select>
                </div>
                <div>
                  <Label>Currency</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={workspace.currency} onChange={(e) => setWorkspace({ ...workspace, currency: e.target.value })}>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <div>
                  <Label>Brand Color</Label>
                  <Input type="color" value={workspace.brand_color} onChange={(e) => setWorkspace({ ...workspace, brand_color: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveWorkspace} disabled={loading} className="bg-[#0176D3] hover:bg-[#0155A8]">{loading ? 'Saving…' : 'Save Workspace'}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users & Teams Tab */}
        <TabsContent value="users-teams">
          <Card>
            <CardHeader>
              <CardTitle>Users & Teams</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 border-2 border-dashed rounded-lg">
                <h4 className="font-medium mb-3">Invite User</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input placeholder="email@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                  <select className="border rounded px-2 py-2" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <Button onClick={sendInvite} className="bg-[#0176D3] hover:bg-[#0155A8]">Send Invite</Button>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Team Members</h4>
                {invites.length === 0 ? (
                  <div className="text-sm text-gray-500">No invites yet.</div>
                ) : (
                  <ul className="text-sm space-y-2">
                    {invites.map((iv) => (
                      <li key={iv.id} className="flex items-center justify-between border rounded px-3 py-2">
                        <span>{iv.email}</span>
                        <span className="text-gray-500">{iv.role} • {iv.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipelines Tab */}
        <TabsContent value="pipelines">
          <Card>
            <CardHeader>
              <CardTitle>Pipelines</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input placeholder="Stage name (e.g., Qualified)" value={stageName} onChange={(e) => setStageName(e.target.value)} />
                  <Input placeholder="Probability %" type="number" value={stageProb} onChange={(e) => setStageProb(e.target.value)} />
                  <Button onClick={addStage} className="bg-[#0176D3] hover:bg-[#0155A8]">Add Stage</Button>
                </div>
                {stages.length === 0 ? (
                  <div className="text-sm text-gray-500">No stages yet.</div>
                ) : (
                  <ul className="text-sm space-y-2">
                    {stages.map((s) => (
                      <li key={s.order} className="flex items-center justify-between border rounded px-3 py-2">
                        <span>{s.order}. {s.name}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-500">{s.probability}%</span>
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => removeStage(s.order)}>Remove</Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex justify-end">
                  <Button onClick={savePipeline} className="bg-[#0176D3] hover:bg-[#0155A8]">Save Pipeline</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit-log">
          <Card>
            <CardHeader>
              <CardTitle>Audit Log</CardTitle>
            </CardHeader>
            <CardContent>
              {auditLoading ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : auditLogs.length === 0 ? (
                <div className="text-sm text-gray-500">No events yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="py-2 pr-4">Time</th>
                        <th className="py-2 pr-4">Action</th>
                        <th className="py-2 pr-4">Entity</th>
                        <th className="py-2 pr-4">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log) => (
                        <tr key={log.id} className="border-b last:border-0">
                          <td className="py-2 pr-4 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                          <td className="py-2 pr-4">{log.action}</td>
                          <td className="py-2 pr-4">{log.entity_type || '-'}{log.entity_id ? `:${log.entity_id}` : ''}</td>
                          <td className="py-2 pr-4 max-w-xl">
                            <pre className="whitespace-pre-wrap break-words text-xs bg-gray-50 p-2 rounded">{JSON.stringify(log.after || log.before || {}, null, 2)}</pre>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#0176D3] text-white text-2xl">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg">{user?.name}</h3>
                  <p className="text-gray-600 text-sm">{user?.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    disabled
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={profileData.company}
                    onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <select
                    id="timezone"
                    value={profileData.timezone}
                    onChange={(e) => setProfileData({ ...profileData, timezone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleProfileSave} 
                  disabled={loading}
                  className="bg-[#0176D3] hover:bg-[#0155A8]"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* API Keys Tab */}
        <TabsContent value="api-keys">
          <Card>
            <CardHeader>
              <CardTitle>API Keys</CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Manage API keys for integrating Bissa.ai with external applications
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Create New API Key */}
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg">
                <h4 className="font-medium mb-3">Generate New API Key</h4>
                <div className="flex gap-3">
                  <Input
                    placeholder="API key name (e.g., LeadGen Pro Integration)"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleCreateApiKey}
                    className="bg-[#0176D3] hover:bg-[#0155A8]"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Generate Key
                  </Button>
                </div>
              </div>

              {/* Show newly created key */}
              {showNewKey && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-800 mb-2">
                        🎉 API Key Created Successfully!
                      </h4>
                      <p className="text-sm text-green-700 mb-3">
                        Copy this key now - it won't be shown again for security reasons.
                      </p>
                      <div className="flex items-center gap-2 p-3 bg-white border border-green-300 rounded font-mono text-sm">
                        <code className="flex-1 break-all">{showNewKey.key}</code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(showNewKey.key)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewKey(null)}
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              )}

              {/* Existing API Keys */}
              <div>
                <h4 className="font-medium mb-3">Your API Keys</h4>
                {apiKeys.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Key className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No API keys yet. Generate one to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {apiKeys.map((apiKey) => (
                      <div
                        key={apiKey.id}
                        className="p-4 border rounded-lg hover:border-gray-400 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="font-semibold text-gray-900">{apiKey.name}</h5>
                              {apiKey.is_active ? (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded">
                                  Inactive
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 mb-2">
                              <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                                {apiKey.key.includes('*') || !visibleKeys[apiKey.id] ? (apiKey.key.includes('*') ? apiKey.key : maskApiKey(apiKey.key)) : apiKey.key}
                              </code>
                              {/* Keys are stored hashed: only keys not yet migrated can be revealed or copied; copy the full key when you create it. */}
                              {!apiKey.key.includes('*') && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleKeyVisibility(apiKey.id)}
                                  >
                                    {visibleKeys[apiKey.id] ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => copyToClipboard(apiKey.key)}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>

                            <div className="text-xs text-gray-600 space-y-1">
                              <p>Created: {formatDate(apiKey.created_at)}</p>
                              <p>Last used: {formatDate(apiKey.last_used)}</p>
                            </div>
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteApiKey(apiKey.id)}
                            className="text-red-600 hover:text-red-700 ml-4"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* API Documentation Link */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">📚 API Documentation</h4>
                <p className="text-sm text-blue-800 mb-3">
                  Learn how to integrate Bissa.ai with your applications using our REST API.
                </p>
                <div className="space-y-2 text-sm">
                  <p className="text-blue-700">
                    <strong>Base URL:</strong> <code className="bg-white px-2 py-0.5 rounded">{BACKEND_URL}/api</code>
                  </p>
                  <p className="text-blue-700">
                    <strong>Authentication:</strong> Include <code className="bg-white px-2 py-0.5 rounded">X-API-Key</code> header
                  </p>
                  <p className="text-blue-700">
                    <strong>Rate Limit:</strong> 100 requests per minute
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="mt-3"
                  onClick={() => window.open('/LEADSPOT_API_INTEGRATION.md', '_blank')}
                >
                  View Full Documentation
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Email Notifications</h4>
                    <p className="text-sm text-gray-600">Receive email updates about your account</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.emailNotifications}
                    onChange={(e) => setNotifications({ ...notifications, emailNotifications: e.target.checked })}
                    className="w-5 h-5 text-[#0176D3] rounded focus:ring-[#0176D3]"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Deal Updates</h4>
                    <p className="text-sm text-gray-600">Get notified when deals change stages</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.dealUpdates}
                    onChange={(e) => setNotifications({ ...notifications, dealUpdates: e.target.checked })}
                    className="w-5 h-5 text-[#0176D3] rounded focus:ring-[#0176D3]"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Campaign Alerts</h4>
                    <p className="text-sm text-gray-600">Notifications about campaign performance</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.campaignAlerts}
                    onChange={(e) => setNotifications({ ...notifications, campaignAlerts: e.target.checked })}
                    className="w-5 h-5 text-[#0176D3] rounded focus:ring-[#0176D3]"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Weekly Report</h4>
                    <p className="text-sm text-gray-600">Receive weekly summary of your activities</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.weeklyReport}
                    onChange={(e) => setNotifications({ ...notifications, weeklyReport: e.target.checked })}
                    className="w-5 h-5 text-[#0176D3] rounded focus:ring-[#0176D3]"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleNotificationsSave} 
                  disabled={loading}
                  className="bg-[#0176D3] hover:bg-[#0155A8]"
                >
                  {loading ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <Card>
            <CardHeader>
              <CardTitle>Application Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dateFormat">Date Format</Label>
                  <select
                    id="dateFormat"
                    value={preferences.dateFormat}
                    onChange={(e) => setPreferences({ ...preferences, dateFormat: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <select
                    id="currency"
                    value={preferences.currency}
                    onChange={(e) => setPreferences({ ...preferences, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="language">Language</Label>
                  <select
                    id="language"
                    value={preferences.language}
                    onChange={(e) => {
                      const newLang = e.target.value;
                      setPreferences({ ...preferences, language: newLang });
                      setLanguage(newLang);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="sw">Swahili</option>
                    <option value="ar">Arabic</option>
                    <option value="pt">Portuguese</option>
                    <option value="ha">Hausa</option>
                    <option value="am">Amharic</option>
                    <option value="yo">Yoruba</option>
                    <option value="ig">Igbo</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="theme">Theme</Label>
                  <select
                    id="theme"
                    value={preferences.theme}
                    onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handlePreferencesSave} 
                  disabled={loading}
                  className="bg-[#0176D3] hover:bg-[#0155A8]"
                >
                  {loading ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Account Security</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Your account is secured with Google OAuth. You can manage your security settings through your Google account.
                  </p>
                  <Button variant="outline">
                    Manage Google Account
                  </Button>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Active Sessions</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    You're currently signed in. Your session will expire in 7 days.
                  </p>
                  <Button variant="outline" className="text-red-600 hover:text-red-700">
                    Sign Out All Devices
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Tab */}
        <TabsContent value="data">
          <Card>
            <CardHeader>
              <CardTitle>Data Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Export Your Data</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Download a copy of all your contacts, companies, deals, and campaigns.
                  </p>
                  <Button 
                    onClick={handleExportData}
                    className="bg-[#0176D3] hover:bg-[#0155A8]"
                  >
                    Export Data
                  </Button>
                </div>

                <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                  <h4 className="font-medium mb-2 text-red-800">Delete Account</h4>
                  <p className="text-sm text-red-600 mb-4">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <Button variant="outline" className="text-red-600 hover:text-red-700 border-red-300">
                    Delete Account
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Email Templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Create template */}
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg">
                <h4 className="font-medium mb-3">Create New Template</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label>Name</Label>
                    <Input value={newTpl.name} onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Input value={newTpl.subject} onChange={(e) => setNewTpl({ ...newTpl, subject: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Body (HTML)</Label>
                    <textarea className="w-full border rounded-md p-2 min-h-[120px]" value={newTpl.body_html} onChange={(e) => setNewTpl({ ...newTpl, body_html: e.target.value })} placeholder="Use variables like {{contact.name}}" />
                  </div>
                  <div>
                    <Label>Body (Text)</Label>
                    <textarea className="w-full border rounded-md p-2 min-h-[120px]" value={newTpl.body_text} onChange={(e) => setNewTpl({ ...newTpl, body_text: e.target.value })} />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <Button onClick={createTemplate} className="bg-[#0176D3] hover:bg-[#0155A8]">Save Template</Button>
                </div>
              </div>

              {/* List templates */}
              <div>
                <h4 className="font-medium mb-3">Your Templates</h4>
                {tplLoading ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : templates.length === 0 ? (
                  <p className="text-sm text-gray-500">No templates yet.</p>
                ) : (
                  <div className="space-y-3">
                    {templates.map((tpl) => (
                      <div key={tpl.id} className="p-4 border rounded-lg">
                        {editingTpl && editingTpl.id === tpl.id ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label>Name</Label>
                                <Input value={editingTpl.name} onChange={(e) => setEditingTpl({ ...editingTpl, name: e.target.value })} />
                              </div>
                              <div>
                                <Label>Subject</Label>
                                <Input value={editingTpl.subject} onChange={(e) => setEditingTpl({ ...editingTpl, subject: e.target.value })} />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label>Body (HTML)</Label>
                                <textarea className="w-full border rounded-md p-2 min-h-[120px]" value={editingTpl.body_html || ''} onChange={(e) => setEditingTpl({ ...editingTpl, body_html: e.target.value })} />
                              </div>
                              <div>
                                <Label>Body (Text)</Label>
                                <textarea className="w-full border rounded-md p-2 min-h-[120px]" value={editingTpl.body_text || ''} onChange={(e) => setEditingTpl({ ...editingTpl, body_text: e.target.value })} />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" onClick={() => setEditingTpl(null)}>Cancel</Button>
                              <Button onClick={saveTemplate} className="bg-[#0176D3] hover:bg-[#0155A8]">Save</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h5 className="font-semibold">{tpl.name}</h5>
                              <div className="text-sm text-gray-600">Subject: {tpl.subject}</div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => setEditingTpl(tpl)}>Edit</Button>
                              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => deleteTemplate(tpl.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="custom-fields">
          <Card>
            <CardHeader>
              <CardTitle>Custom Fields</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg">
                <h4 className="font-medium mb-3">Add New Field</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Entity</Label>
                    <select className="w-full border rounded px-2 py-2" value={newField.entity} onChange={(e) => setNewField({ ...newField, entity: e.target.value })}>
                      <option value="contact">Contact</option>
                      <option value="company">Company</option>
                      <option value="deal">Deal</option>
                    </select>
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input value={newField.name} onChange={(e) => setNewField({ ...newField, name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <select className="w-full border rounded px-2 py-2" value={newField.type} onChange={(e) => setNewField({ ...newField, type: e.target.value })}>
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="dropdown">Dropdown</option>
                      <option value="date">Date</option>
                    </select>
                  </div>
                  <div>
                    <Label>Options (comma for dropdown)</Label>
                    <Input value={newField.options} onChange={(e) => setNewField({ ...newField, options: e.target.value })} />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <Button onClick={createCustomField} className="bg-[#0176D3] hover:bg-[#0155A8]">Add Field</Button>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Existing Fields</h4>
                {customFields.length === 0 ? (
                  <p className="text-sm text-gray-500">No custom fields yet.</p>
                ) : (
                  <div className="space-y-2">
                    {customFields.map((f) => (
                      <div key={f.id} className="p-3 border rounded flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">[{f.entity}]</span> {f.name} • <span className="uppercase text-xs">{f.type}</span>
                        </div>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => deleteCustomField(f.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <Card>
            <CardHeader>
              <CardTitle>Google Integrations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <GoogleIntegrations />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Help & Support Tab */}
        <TabsContent value="help">
          <HelpDocumentation />
        </TabsContent>
      </Tabs>

      {/* Tutorial Modal */}
      <TutorialModal 
        isOpen={isTutorialOpen}
        onClose={() => setIsTutorialOpen(false)}
      />

      {/* Contact Support Modal */}
      <ContactSupportModal 
        isOpen={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
        user={user}
      />
    </div>
  );
};

export default Settings;

// Local component for integrations
function GoogleIntegrations() {
  const [status, setStatus] = React.useState({ connected: false, scopes: [] });
  const [loading, setLoading] = React.useState(false);
  const [events, setEvents] = React.useState([]);
  const [messages, setMessages] = React.useState([]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/integrations/google/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      // ignore
    }
  };

  React.useEffect(() => {
    fetchStatus();
  }, []);

  const connect = async () => {
    window.location.href = `${API}/integrations/google/connect?redirect=${encodeURIComponent(window.location.origin + '/settings')}`;
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/calendar/events`, { withCredentials: true });
      setEvents(res.data.events || []);
    } catch (e) {}
    setLoading(false);
  };

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gmail/messages`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (e) {}
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="p-4 border rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">Google Account</div>
            <div className="text-sm text-gray-600">Calendar events and read-only Gmail</div>
            <div className="text-xs text-gray-500 mt-1">Scopes: {status.scopes?.join(', ')}</div>
          </div>
          {status.connected ? (
            <span className="px-2 py-1 rounded bg-green-100 text-green-800 text-xs">Connected</span>
          ) : (
            <Button variant="outline" onClick={connect}>Connect Google</Button>
          )}
        </div>
      </div>

      {status.connected && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">Upcoming Events</div>
              <Button variant="outline" size="sm" onClick={fetchEvents} disabled={loading}>Refresh</Button>
            </div>
            {events.length === 0 ? (
              <div className="text-sm text-gray-500">No events.</div>
            ) : (
              <ul className="space-y-2 text-sm">
                {events.map((e, idx) => (
                  <li key={idx} className="border rounded p-2">
                    <div className="font-medium">{e.summary || '(no title)'}</div>
                    <div className="text-gray-600">{e.start?.dateTime || e.start?.date} → {e.end?.dateTime || e.end?.date}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">Recent Emails</div>
              <Button variant="outline" size="sm" onClick={fetchEmails} disabled={loading}>Refresh</Button>
            </div>
            {messages.length === 0 ? (
              <div className="text-sm text-gray-500">No emails.</div>
            ) : (
              <ul className="space-y-2 text-sm">
                {messages.map((m) => (
                  <li key={m.id} className="border rounded p-2">
                    <div className="font-medium">{m.subject || '(no subject)'}</div>
                    <div className="text-gray-600">{m.from} • {m.date}</div>
                    <div className="text-gray-500 line-clamp-2">{m.snippet}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}